import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

const keyMap: Record<string, string> = {
  email: "email_verified",
  phone: "phone_verified",
  profile: "profile_completed",
  consent: "consent_given",
};

export function normalizeKey(key: string): string {
  return keyMap[key] || key;
}

export interface TierDefinition {
  id: string;
  tier_level: number;
  name: string;
  requirements_json: string[];
  card_deposit_limit: number;
  wire_deposit_limit: number;
  crypto_deposit_limit: number;
  wire_withdrawal_limit: number;
  crypto_withdrawal_limit: number;
  enabled: boolean;
}

export interface UserVerification {
  id: string;
  verification_type: string;
  status: string;
  submission_data: any;
  rejection_reason: string | null;
  admin_notes?: string | null;
  visible_to_user?: boolean | null;
  created_at: string;
  updated_at: string;
}

export function useUserTier() {
  const { user } = useAuth();
  const { isSandbox } = usePlatformSettings();
  const [currentTier, setCurrentTier] = useState(0);
  const [tiers, setTiers] = useState<TierDefinition[]>([]);
  const [verifications, setVerifications] = useState<UserVerification[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    try {
      const [tiersRes, verificationsRes, profileRes] = await Promise.all([
        supabase.from("verification_tiers").select("*").eq("enabled", true).order("tier_level"),
        supabase.from("user_verifications").select("*").eq("user_id", user.id),
        supabase.from("user_profiles").select("*").eq("user_id", user.id).single(),
      ]);
      setTiers((tiersRes.data as any[]) || []);
      setVerifications((verificationsRes.data as any[]) || []);
      setProfile(profileRes.data);
      setCurrentTier(profileRes.data?.verification_tier || 0);
    } catch {
      // Ensure loading resets even on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]);

  const getVerificationStatus = (type: string) => {
    const normalized = normalizeKey(type);
    const v = verifications.find((v) => v.verification_type === normalized);
    return v?.status || null;
  };

  const isRequirementMet = (type: string): boolean => {
    const normalized = normalizeKey(type);
    // In live mode, phone verification is paused — treat phone reqs as auto-met.
    if (normalized === "phone_verified" && !isSandbox) return true;
    // Auto-detected requirements
    if (normalized === "email_verified") return !!user?.email_confirmed_at;
    if (normalized === "phone_verified") return !!profile?.phone;
    if (normalized === "profile_completed") {
      return !!(profile?.full_name && profile?.address && profile?.dob && profile?.country);
    }
    // DB-tracked requirements
    return getVerificationStatus(type) === "approved";
  };

  const activeTier = tiers.find((t) => t.tier_level === currentTier) || null;

  return {
    currentTier,
    activeTier,
    tiers,
    verifications,
    profile,
    loading,
    isRequirementMet,
    getVerificationStatus,
    isSandbox,
    reload: load,
  };
}
